import { FormGroup, FormControl, FormArray } from "@angular/forms";
import { DeviceBase } from "./device-base";
import { PropertyMetadata } from "../../properties/property-types";
import { RegisterElement } from "../element-decorator";
import { ParkingRowElement } from "../parking/parkingrow";
import { LevelDesignerElementBase } from "../element-base";
import { IClonable } from "../interfaces/clonable";
import { v4 as uuidv4 } from 'uuid';
import { ParkingSpaceElement } from "../parking/parkingspace";

@RegisterElement()
export class GuidanceLight extends DeviceBase implements IClonable {
    protected sizeInMeters: number = 1;
    public override TypeName: string = "GuidanceLight";
    public override ParentType: string | null = "ParkingRow";
    public override ParentFormGroupName: string | null = "GuidanceLightConfigurations";
    public isClonable: boolean = true;
    public cloneDistance: number = 4; // 4 meters between guidance lights
    private portAssignments: Map<number, ParkingSpaceElement | null> = new Map();
    private isDraggingPort: boolean = false;
    private draggedPortNumber: number = -1;
    private dragEndPoint: { x: number, y: number } | null = null;
    private hoveredSpace: ParkingSpaceElement | null = null;
    private hoveredPortNumber: number = -1;
    private readonly portSize: number = 0.3; // Size of the port squares in meters

    constructor(formGroup: FormGroup) {
        super(formGroup, 'assets/icons/guidancelight.svg');

        // Initialize port assignments form array if it doesn't exist
        if (!formGroup.contains('PortAssignments')) {
            formGroup.addControl('PortAssignments', new FormArray([]));
            // Initialize 6 empty port assignments
            for (let i = 1; i <= 6; i++) {
                (formGroup.get('PortAssignments') as FormArray).push(new FormGroup({
                    ParkingSpaceId: new FormControl(null),
                    PortNumber: new FormControl(i)
                }));
            }
        }
        else if (!(formGroup.get('PortAssignments') instanceof FormArray)) {
            const existingValue = formGroup.get('PortAssignments')?.value;
            formGroup.removeControl('PortAssignments');
            formGroup.addControl('PortAssignments', new FormArray([]));
        }

        // Ensure all port numbers 1-6 exist in PortAssignments
        const portAssignments = formGroup.get('PortAssignments') as FormArray;
        for (let portNumber = 1; portNumber <= 6; portNumber++) {
            // Check if this port number exists
            const hasPort = portAssignments.controls.some(control => 
                (control as FormGroup).get('PortNumber')?.value === portNumber
            );

            // If port number doesn't exist, add it
            if (!hasPort) {
                portAssignments.push(new FormGroup({
                    ParkingSpaceId: new FormControl(null),
                    PortNumber: new FormControl(portNumber)
                }));
            }
        }

        const idValue = formGroup.get('Id')?.value;
        if (idValue && typeof idValue !== 'number') {
            formGroup.get('Id')?.setValue(null);
        }
        // Initialize ChipId if it doesn't exist
        if (!formGroup.contains('ChipId')) {
            formGroup.addControl('ChipId', new FormControl(null));
        }
        if (!formGroup.contains('VacantColor')) {
            formGroup.addControl('VacantColor', new FormControl('00ff00'));
        }
        // Initialize brightness control
        if (!formGroup.contains('Brightness')) {
            formGroup.addControl('Brightness', new FormControl(2)); // Default to 100%
        }

        // Initialize mode control 
        if (!formGroup.contains('Mode')) {
            formGroup.addControl('Mode', new FormControl(0)); // Default to 0
        }

        // Initialize always show vacant color flag
        if (!formGroup.contains('AlwaysShowVacantColor')) {
            formGroup.addControl('AlwaysShowVacantColor', new FormControl(false));
        }

        // Initialize reserved color
        if (!formGroup.contains('ReservedColor')) {
            formGroup.addControl('ReservedColor', new FormControl('00ff00')); // Default red
        }

        // Initialize reserved brightness
        if (!formGroup.contains('ReservedBrightness')) {
            formGroup.addControl('ReservedBrightness', new FormControl(2)); // Default to 100%
        }
        // Initialize schedule ID
        if (!formGroup.contains('ScheduleId')) {
            formGroup.addControl('ScheduleId', new FormControl(null));
        }
    }

    private getPortPosition(portNumber: number): { x: number, y: number } {
        // Calculate base positions in a bolt-hole pattern around the guidance light
        const offset = this.sizeInMeters * 0.7; // Distance from center
        let baseX: number;
        let baseY: number;
        
        switch(portNumber) {
            case 1: 
                baseX = offset;
                baseY = -offset;
                break;
            case 2: 
                baseX = offset;
                baseY = 0;
                break;
            case 3: 
                baseX = offset;
                baseY = offset;
                break;
            case 4: 
                baseX = -offset;
                baseY = offset;
                break;
            case 5: 
                baseX = -offset;
                baseY = 0;
                break;
            case 6: 
                baseX = -offset;
                baseY = -offset;
                break;
            default: 
                baseX = 0;
                baseY = 0;
        }

        // Rotate the point around the center by DisplayAngle
        const angleRad = this.DisplayAngle * Math.PI / 180;
        const cos = Math.cos(angleRad);
        const sin = Math.sin(angleRad);
        const rotatedX = baseX * cos - baseY * sin;
        const rotatedY = baseX * sin + baseY * cos;

        // Translate to final position
        return {
            x: this.x + rotatedX,
            y: this.y + rotatedY
        };
    }

    public override draw(ctx: CanvasRenderingContext2D): void {
        // Call base class draw method first
        super.draw(ctx);

        // First draw all assignment lines
        for (let portNumber = 1; portNumber <= 6; portNumber++) {
            const portPos = this.getPortPosition(portNumber);
            const space = this.portAssignments.get(portNumber);

            const spaceIsValid = space != null && space.getBoundingBox().width > 0;
            if (spaceIsValid || (this.isDraggingPort && this.draggedPortNumber === portNumber)) {
                const endPoint = this.isDraggingPort && this.draggedPortNumber === portNumber ? 
                    this.dragEndPoint! : 
                    this.getSpaceCenter(space!);

                // Draw dotted line
                ctx.save();
                ctx.beginPath();
                ctx.moveTo(portPos.x, portPos.y);
                ctx.lineTo(endPoint.x, endPoint.y);
                ctx.setLineDash([0.2, 0.2]);
                ctx.strokeStyle = '#666666';
                ctx.lineWidth = 0.1;
                ctx.stroke();
                ctx.restore(); // This will restore the original line dash state
            }
        }

        // Draw highlight around hovered space during drag
        if (this.isDraggingPort && this.hoveredSpace) {
            ctx.save();
            ctx.beginPath();
            const bbox = this.hoveredSpace.getBoundingBox();
            ctx.rect(bbox.x, bbox.y, bbox.width, bbox.height);
            ctx.strokeStyle = '#00ff00';
            ctx.lineWidth = 0.2;
            ctx.stroke();
            ctx.restore();
        }

        // Draw auto-assign button
        const buttonSize = this.sizeInMeters * 0.4;
        const buttonX = this.x - buttonSize / 2;
        const buttonY = this.y - this.sizeInMeters - buttonSize;
        
        ctx.save();
        ctx.fillStyle = '#4CAF50';
        ctx.strokeStyle = '#45A049';
        ctx.lineWidth = 0.05;
        
        // Draw button background
        ctx.beginPath();
        ctx.rect(buttonX, buttonY, buttonSize, buttonSize);
        ctx.fill();
        ctx.stroke();
        
        // Draw asterisk
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 0.02;
        const center = {
            x: buttonX + buttonSize / 2,
            y: buttonY + buttonSize / 2
        };
        const radius = buttonSize * 0.25;
        
        // Draw 8-point asterisk
        for (let i = 0; i < 8; i++) {
            const angle = (i * Math.PI) / 4;
            ctx.beginPath();
            ctx.moveTo(
                center.x + Math.cos(angle) * radius,
                center.y + Math.sin(angle) * radius
            );
            ctx.lineTo(
                center.x - Math.cos(angle) * radius,
                center.y - Math.sin(angle) * radius
            );
            ctx.stroke();
        }
        
        ctx.restore();

        // Then draw all port squares and numbers on top
        for (let portNumber = 1; portNumber <= 6; portNumber++) {
            const portPos = this.getPortPosition(portNumber);
            const isHovered = this.hoveredPortNumber === portNumber;

            // Draw port square
            ctx.save();
            ctx.beginPath();

            // Translate to port position and rotate square
            ctx.translate(portPos.x, portPos.y);
            ctx.rotate(this.DisplayAngle * Math.PI / 180);
            
            // Draw the square centered at origin (0,0) after translation
            ctx.rect(
                -this.portSize/2,
                -this.portSize/2,
                this.portSize,
                this.portSize
            );
            ctx.fillStyle = isHovered ? '#000000' : '#ffffff';
            ctx.strokeStyle = '#666666';
            ctx.fill();
            ctx.stroke();

            // Reset rotation for the number to keep it upright
            ctx.rotate(-this.DisplayAngle * Math.PI / 180);
            
            // Draw port number
            ctx.fillStyle = isHovered ? '#ffffff' : '#000000';
            ctx.font = '0.56px Arial'; // This is the minimum font size for Firefox that is displayed
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(portNumber.toString(), 0, 0);

            ctx.restore();
        }
    }

    private getSpaceCenter(space: ParkingSpaceElement): { x: number, y: number } {
        const bbox = space.getBoundingBox();
        return {
            x: bbox.x + bbox.width / 2,
            y: bbox.y + bbox.height / 2
        };
    }

    public override handleMouseDown(x: number, y: number): boolean {
        // Check if click is on auto-assign button
        if (this.isPointInAutoAssignButton(x, y)) {
            this.autoAssignSpaces();
            return true;
        }

        // Check if click is within any port square
        for (let portNumber = 1; portNumber <= 6; portNumber++) {
            const portPos = this.getPortPosition(portNumber);
            if (Math.abs(x - portPos.x) <= this.portSize/2 &&
                Math.abs(y - portPos.y) <= this.portSize/2) {
                this.isDraggingPort = true;
                this.draggedPortNumber = portNumber;
                this.dragEndPoint = { x, y };
                // Clear the port assignment when starting to drag
                this.updatePortAssignment(portNumber, null);
                return true;
            }
        }
        return super.handleMouseDown(x, y);
    }

    public override handleMouseMove(x: number, y: number): boolean {
        const startTime = performance.now();
        
        if (this.isDraggingPort) {
            this.dragEndPoint = { x, y };
            
            // Only check for hovered space if we're not already hovering over one
            // or if we've moved significantly from the last hovered space
            if (!this.hoveredSpace || 
                !this.isPointInSpace(x, y, this.hoveredSpace)) {
                
                // Check if mouse is over a parking space
                const parkingRow = this.currentParentElement as ParkingRowElement;
                if (parkingRow) {
                    // Find the first space that contains the point
                    this.hoveredSpace = parkingRow.elements.find(element => 
                        element instanceof ParkingSpaceElement && 
                        this.isPointInSpace(x, y, element)
                    ) as ParkingSpaceElement || null;
                }
            }
            return true;
        } else {
            // Check if mouse is over any port square
            const wasHovered = this.hoveredPortNumber !== -1;
            this.hoveredPortNumber = -1;
            for (let portNumber = 1; portNumber <= 6; portNumber++) {
                const portPos = this.getPortPosition(portNumber);
                if (Math.abs(x - portPos.x) <= this.portSize/2 &&
                    Math.abs(y - portPos.y) <= this.portSize/2) {
                    this.hoveredPortNumber = portNumber;
                    const endTime = performance.now();
                    return true;
                }
            }
            if (wasHovered) {
                const endTime = performance.now();
                return true;
            }
        }

        
        return false;
    }

    private isPointInSpace(x: number, y: number, space: ParkingSpaceElement): boolean {
        const bbox = space.getBoundingBox();
        return x >= bbox.x && x <= bbox.x + bbox.width &&
               y >= bbox.y && y <= bbox.y + bbox.height;
    }

    public override handleMouseUp(x: number, y: number): boolean {
        if (this.isDraggingPort) {
            if (this.hoveredSpace) {
                this.updatePortAssignment(this.draggedPortNumber, this.hoveredSpace);
            }
            this.isDraggingPort = false;
            this.draggedPortNumber = -1;
            this.dragEndPoint = null;
            this.hoveredSpace = null;
            return true;
        }
        return super.handleMouseUp(x, y);
    }

    public updatePortAssignment(portNumber: number, parkingSpace: ParkingSpaceElement | null): void {
        this.portAssignments.set(portNumber, parkingSpace);
        
        // Update form control
        const portAssignments = this.formGroup.get('PortAssignments') as FormArray;
        // Find the control with matching port number
        const portControl = portAssignments.controls.find(control => 
            (control as FormGroup).get('PortNumber')?.value === portNumber
        );
        if (!portControl) {
            console.error(`Could not find port assignment control for port ${portNumber}`);
            return;
        }
        portControl.get('ParkingSpaceId')?.setValue(parkingSpace?.formGroup.get('Id')?.value || null);
        portControl.markAsDirty();
    }

    public getPortAssignment(portNumber: number): ParkingSpaceElement | null {
        return this.portAssignments.get(portNumber) || null;
    }

    public override getPropertyMetadata(): PropertyMetadata[] {
        const baseMetadata = super.getPropertyMetadata();
        
        // Add port assignment properties
        const portMetadata: PropertyMetadata[] = [];
        const portAssignments = this.formGroup.get('PortAssignments') as FormArray;
        
        // Create metadata for each port assignment based on its PortNumber
        portAssignments.controls.forEach((control) => {
            const portControl = control as FormGroup;
            const portNumber = portControl.get('PortNumber')?.value;
            if (portNumber >= 1 && portNumber <= 6) {
                portMetadata.push({
                    formControlName: `PortAssignments.${portNumber-1}.ParkingSpaceId`,
                    displayName: `Port ${portNumber} Assignment`,
                    inputType: {
                        type: 'select',
                        options: () => {
                            const spacesArray = this.currentParentElement?.formGroup.get('Spaces') as FormArray;
                            if (!spacesArray) return [];
                            
                            return spacesArray.controls.map(control => ({
                                value: control.get('Id')?.value,
                                label: control.get('Name')?.value || 'Unnamed Space'
                            }));
                        }
                    },
                    order: 10 + portNumber,
                    category: 'Port Assignments',
                    hideOnMultipleSelection: true,  // Hide port assignments when multiple elements are selected
                    getValue: (element: LevelDesignerElementBase) => {
                        const light = element as GuidanceLight;
                        const portAssignments = light.formGroup.get('PortAssignments') as FormArray;
                        const portControl = portAssignments.controls.find(c => 
                            (c as FormGroup).get('PortNumber')?.value === portNumber
                        );
                        return portControl?.get('ParkingSpaceId')?.value || '';
                    },
                    setValue: (element: LevelDesignerElementBase, value: string) => {
                        const light = element as GuidanceLight;
                        const parkingRow = light.currentParentElement as ParkingRowElement;
                        if (parkingRow) {
                            const space = parkingRow.elements.find(e => 
                                e instanceof ParkingSpaceElement && 
                                e.formGroup.get('Id')?.value === value
                            ) as ParkingSpaceElement;
                            light.updatePortAssignment(portNumber, space || null);
                        }
                    }
                });
            }
        });

        // Add light type and display mode metadata
        const additionalMetadata: PropertyMetadata[] = [
            {
                formControlName: 'Mode',
                displayName: 'Mode',
                inputType: {
                    type: 'select',
                    options: [
                        { value: 0, label: 'Polling' },
                        { value: 1, label: 'Device' },
                        { value: 2, label: 'Polling Master' },
                        { value: 3, label: 'Device Master' },
                        { value: 4, label: 'Slave' }
                    ]
                },
                order: 1,
                category: 'Configuration'
            },
            {
                formControlName: 'Brightness',
                displayName: 'Brightness',
                inputType: {
                    type: 'select', 
                    options: [
                        { value: 0, label: 'Off' },
                        { value: 1, label: 'Low' },
                        { value: 2, label: 'Medium' },
                        { value: 3, label: 'High' },
                        { value: 4, label: 'Max' }
                    ]
                },
                order: 2,
                category: 'Display'
            },
            {
                formControlName: 'ReservedBrightness',
                displayName: 'Reserved Brightness',
                inputType: {
                    type: 'select', 
                    options: [
                        { value: 0, label: 'Off' },
                        { value: 1, label: 'Low' },
                        { value: 2, label: 'Medium' },
                        { value: 3, label: 'High' },
                        { value: 4, label: 'Max' }
                    ]
                },
                order: 3,
                category: 'Reserved'
            },
            {
                formControlName: 'VacantColor',
                displayName: 'Vacant Color',
                inputType: {
                    type: 'color'
                },
                order: 4,
                category: 'Display'
            },
            {
                formControlName: 'ReservedColor',
                displayName: 'Reserved Color',
                inputType: {
                    type: 'color'
                },
                order: 5,
                category: 'Reserved'
            },
            {
                formControlName: 'OverrideOccupiedColor',
                displayName: 'Stay Reserved Color',
                inputType: {
                    type: 'boolean'
                },
                order: 6,
                category: 'Reserved'
            },
            {
                formControlName: 'DisplayMode',
                displayName: 'Display Mode',
                inputType: { 
                    type: 'select',
                    options: [
                        { value: 'standard', label: 'Standard' },
                        { value: 'arrow', label: 'Arrow' }
                    ]
                },
                order: 8,
                category: 'Display'
            },
            {
                formControlName: 'ChipId',
                displayName: 'Chip ID',
                inputType: { 
                    type: 'searchable-select',
                    url: '/hardware/guidancelights/available/searches',
                    idProperty: 'ChipId',
                    labelProperty: 'ChipId',
                    minSearchLength: 2
                },
                order: 9,
                category: 'Configuration',
                hideOnMultipleSelection: true,
            },
            {
                formControlName: 'ScheduleId',
                displayName: 'Schedule',
                inputType: {
                    type: 'api-select',
                    url: '/calendar/schedules',
                    idProperty: 'Id',
                    labelProperty: 'Name',
                    includeNoneOption: true
                },
                order: 1,
                category: 'Reserved'
            }
        ];

        return [...baseMetadata, ...additionalMetadata, ...portMetadata];
    }

    public override clone(): GuidanceLight {
        return new GuidanceLight(this.formGroup);
    }

    public duplicate(fromElement?: IClonable): IClonable {
        // Create new form group with copied values
        const newFormGroup = new FormGroup({
            Id: new FormControl(uuidv4()),
            Name: new FormControl(this.formGroup.get('Name')?.value || ''),
            DeviceId: new FormControl(this.formGroup.get('DeviceId')?.value || ''),
            DisplayAngle: new FormControl(this.formGroup.get('DisplayAngle')?.value || 0),
            LocationPoints: new FormControl(this.formGroup.get('LocationPoints')?.value || []),
            LightType: new FormControl(this.formGroup.get('LightType')?.value || 'single'),
            DisplayMode: new FormControl(this.formGroup.get('DisplayMode')?.value || 'standard'),
            Enabled: new FormControl(this.formGroup.get('Enabled')?.value ?? true),
            PortAssignments: new FormArray([])
        });

        // Initialize port assignments
        for (let i = 1; i <= 6; i++) {
            (newFormGroup.get('PortAssignments') as FormArray).push(new FormGroup({
                ParkingSpaceId: new FormControl(null),
                PortNumber: new FormControl(i)
            }));
        }

        // Create new instance with the duplicated form group
        const duplicate = new GuidanceLight(newFormGroup);
        return duplicate;
    }

    public override default(): GuidanceLight {
        return this;
    }

    public getPoints(): { x: number, y: number }[] {
        // Get the center point from LocationPoints
        const locationPoints = this.formGroup.get('LocationPoints')?.value || [0, 0];
        const centerX = locationPoints[0] * 1000; // Convert from km to m
        const centerY = locationPoints[1] * 1000;

        // Create points for the corners of the bounding box
        const halfSize = this.sizeInMeters / 2;
        return [
            { x: centerX - halfSize, y: centerY - halfSize }, // Top-left
            { x: centerX + halfSize, y: centerY - halfSize }, // Top-right
            { x: centerX + halfSize, y: centerY + halfSize }, // Bottom-right
            { x: centerX - halfSize, y: centerY + halfSize }  // Bottom-left
        ];
    }

    public setPoints(points: { x: number, y: number }[]): void {
        if (points.length > 0) {
            // Calculate the center point from the first point
            // Since we're a point element, we only care about where the center should be
            const centerX = points[0].x;
            const centerY = points[0].y;
            
            // Update the LocationPoints in the form (converting back to km)
            this.formGroup.patchValue({
                LocationPoints: [centerX / 1000, centerY / 1000] // Convert from m to km
            });
            
            // Update the x, y properties inherited from PointElement
            this.x = centerX;
            this.y = centerY;
        }
    }

    public translate(offsetX: number, offsetY: number): void {
        this.x += offsetX;
        this.y += offsetY;
        this.updateForm();
    }

    private autoAssignSpaces(): void {
        const parkingRow = this.currentParentElement as ParkingRowElement;
        if (!parkingRow) return;

        // Get all spaces from the parking row
        const allSpaces = parkingRow.elements.filter(e => e instanceof ParkingSpaceElement) as ParkingSpaceElement[];
        if (allSpaces.length === 0) return;

        // Check for selected spaces first
        const selectedSpaces = allSpaces.filter(space => space.selected);
        const spaces = selectedSpaces.length > 0 ? selectedSpaces : allSpaces;

        // Clear all current assignments
        for (let i = 1; i <= 6; i++) {
            this.updatePortAssignment(i, null);
        }

        // Separate spaces into left and right sides based on the light's rotation
        const leftSpaces: ParkingSpaceElement[] = [];
        const rightSpaces: ParkingSpaceElement[] = [];

        spaces.forEach(space => {
            const spaceCenter = this.getSpaceCenter(space);
            const relativeX = spaceCenter.x - this.x;
            const relativeY = spaceCenter.y - this.y;
            
            // Rotate the point by negative DisplayAngle to align with light's orientation
            const angleRad = -this.DisplayAngle * Math.PI / 180;
            const cos = Math.cos(angleRad);
            const sin = Math.sin(angleRad);
            const rotatedX = relativeX * cos - relativeY * sin;

            // After rotation, spaces with negative X are on the left side (ports 4-6)
            // and spaces with positive X are on the right side (ports 1-3)
            if (rotatedX < 0) {
                leftSpaces.push(space);
            } else {
                rightSpaces.push(space);
            }
        });

        // Sort spaces by distance to their respective ports
        const sortSpacesByDistanceToPort = (spaces: ParkingSpaceElement[], portNumber: number) => {
            return [...spaces].sort((a: ParkingSpaceElement, b: ParkingSpaceElement) => {
                const portPos = this.getPortPosition(portNumber);
                const aCenter = this.getSpaceCenter(a);
                const bCenter = this.getSpaceCenter(b);
                const distA = Math.hypot(aCenter.x - portPos.x, aCenter.y - portPos.y);
                const distB = Math.hypot(bCenter.x - portPos.x, bCenter.y - portPos.y);
                return distA - distB;
            }).filter((space: ParkingSpaceElement) => {
                const portPos = this.getPortPosition(portNumber);
                const spaceCenter = this.getSpaceCenter(space);
                const distance = Math.hypot(spaceCenter.x - portPos.x, spaceCenter.y - portPos.y);
                return distance <= 10; // Only include spaces within 10 meters
            });
        };

        // Handle right side spaces (ports 1-3)
        if (rightSpaces.length === 1) {
            // Single space gets port 2
            this.updatePortAssignment(2, sortSpacesByDistanceToPort(rightSpaces, 2)[0]);
        } else if (rightSpaces.length === 2) {
            // Two spaces get ports 1 and 3
            const sortedForPort1 = sortSpacesByDistanceToPort(rightSpaces, 1);
            this.updatePortAssignment(1, sortedForPort1[0]);
            this.updatePortAssignment(3, sortedForPort1[1]);
        } else if (rightSpaces.length >= 3) {
            if (selectedSpaces.length > 0) {
                // If using selected spaces, prioritize end ports for better distribution
                const sortedForPort1 = sortSpacesByDistanceToPort(rightSpaces, 1);
                this.updatePortAssignment(1, sortedForPort1[0]);
                
                if (rightSpaces.length >= 4) {
                    // With 4 or more selected spaces, use end ports first
                    const remainingForPort3 = rightSpaces.filter(s => s !== sortedForPort1[0]);
                    const sortedForPort3 = sortSpacesByDistanceToPort(remainingForPort3, 3);
                    this.updatePortAssignment(3, sortedForPort3[0]);
                } else {
                    // With 3 selected spaces, use middle port
                    const remainingForPort2 = rightSpaces.filter(s => s !== sortedForPort1[0]);
                    const sortedForPort2 = sortSpacesByDistanceToPort(remainingForPort2, 2);
                    this.updatePortAssignment(2, sortedForPort2[0]);
                    
                    const remainingForPort3 = remainingForPort2.filter(s => s !== sortedForPort2[0]);
                    if (remainingForPort3.length > 0) {
                        const sortedForPort3 = sortSpacesByDistanceToPort(remainingForPort3, 3);
                        this.updatePortAssignment(3, sortedForPort3[0]);
                    }
                }
            } else {
                // Standard behavior for non-selected spaces
                // Assign middle port (2) first
                const sortedForPort2 = sortSpacesByDistanceToPort(rightSpaces, 2);
                this.updatePortAssignment(2, sortedForPort2[0]);
                
                // Assign top port (1)
                const remainingForPort1 = rightSpaces.filter(s => s !== sortedForPort2[0]);
                const sortedForPort1 = sortSpacesByDistanceToPort(remainingForPort1, 1);
                this.updatePortAssignment(1, sortedForPort1[0]);
                
                // Assign bottom port (3)
                const remainingForPort3 = remainingForPort1.filter(s => s !== sortedForPort1[0]);
                const sortedForPort3 = sortSpacesByDistanceToPort(remainingForPort3, 3);
                this.updatePortAssignment(3, sortedForPort3[0]);
            }
        }

        // Handle left side spaces (ports 4-6)
        if (leftSpaces.length === 1) {
            // Single space gets port 5
            this.updatePortAssignment(5, sortSpacesByDistanceToPort(leftSpaces, 5)[0]);
        } else if (leftSpaces.length === 2) {
            // Two spaces get ports 4 and 6
            const sortedForPort4 = sortSpacesByDistanceToPort(leftSpaces, 4);
            this.updatePortAssignment(4, sortedForPort4[0]);
            this.updatePortAssignment(6, sortedForPort4[1]);
        } else if (leftSpaces.length >= 3) {
            if (selectedSpaces.length > 0) {
                // If using selected spaces, prioritize end ports for better distribution
                const sortedForPort4 = sortSpacesByDistanceToPort(leftSpaces, 4);
                this.updatePortAssignment(4, sortedForPort4[0]);
                
                if (leftSpaces.length >= 4) {
                    // With 4 or more selected spaces, use end ports first
                    const remainingForPort6 = leftSpaces.filter(s => s !== sortedForPort4[0]);
                    const sortedForPort6 = sortSpacesByDistanceToPort(remainingForPort6, 6);
                    this.updatePortAssignment(6, sortedForPort6[0]);
                } else {
                    // With 3 selected spaces, use middle port
                    const remainingForPort5 = leftSpaces.filter(s => s !== sortedForPort4[0]);
                    const sortedForPort5 = sortSpacesByDistanceToPort(remainingForPort5, 5);
                    this.updatePortAssignment(5, sortedForPort5[0]);
                    
                    const remainingForPort6 = remainingForPort5.filter(s => s !== sortedForPort5[0]);
                    if (remainingForPort6.length > 0) {
                        const sortedForPort6 = sortSpacesByDistanceToPort(remainingForPort6, 6);
                        this.updatePortAssignment(6, sortedForPort6[0]);
                    }
                }
            } else {
                // Standard behavior for non-selected spaces
                // Assign middle port (5) first
                const sortedForPort5 = sortSpacesByDistanceToPort(leftSpaces, 5);
                this.updatePortAssignment(5, sortedForPort5[0]);
                
                // Assign bottom port (4)
                const remainingForPort4 = leftSpaces.filter(s => s !== sortedForPort5[0]);
                const sortedForPort4 = sortSpacesByDistanceToPort(remainingForPort4, 4);
                this.updatePortAssignment(4, sortedForPort4[0]);
                
                // Assign top port (6)
                const remainingForPort6 = remainingForPort4.filter(s => s !== sortedForPort4[0]);
                const sortedForPort6 = sortSpacesByDistanceToPort(remainingForPort6, 6);
                this.updatePortAssignment(6, sortedForPort6[0]);
            }
        }
    }

    private isPointInAutoAssignButton(x: number, y: number): boolean {
        const buttonSize = this.sizeInMeters * 0.4;
        const buttonX = this.x - buttonSize / 2;
        const buttonY = this.y - this.sizeInMeters - buttonSize;
        
        return x >= buttonX && x <= buttonX + buttonSize &&
               y >= buttonY && y <= buttonY + buttonSize;
    }
} 
